/**
 * header.js — site header interactions
 *
 * Drawer, desktop dropdown keyboard support, scroll shadow, cart badge.
 */

(function () {
  'use strict';

  /* ── DOM refs ─────────────────────────────────────────────── */
  var header          = document.getElementById('site-header');
  var hamburger       = document.querySelector('.site-header__hamburger');
  var drawer          = document.getElementById('site-drawer');
  var drawerClose     = document.querySelector('.site-header__drawer-close');
  var overlay         = document.getElementById('site-header-overlay');

  /* ── Focus trap state ──────────────────────────────────────── */
  var _trapHandler = null;

  /* ════════════════════════════════════════════════════════════
     DRAWER
  ════════════════════════════════════════════════════════════ */

  function openDrawer() {
    if (!drawer || !hamburger) return;

    drawer.removeAttribute('hidden');
    // rAF ensures the hidden removal is painted before transform transition fires
    requestAnimationFrame(function () {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-visible');
    });

    hamburger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('drawer-open');

    // Focus trap
    _installFocusTrap(drawer);

    // Close on Escape
    document.addEventListener('keydown', _onEscapeKey);
  }

  function closeDrawer() {
    if (!drawer || !hamburger) return;

    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-visible');
    hamburger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('drawer-open');
    document.removeEventListener('keydown', _onEscapeKey);
    _removeFocusTrap(drawer);

    // Re-hide after CSS transition completes
    var transitionDuration = 300;
    setTimeout(function () {
      if (!drawer.classList.contains('is-open')) {
        drawer.setAttribute('hidden', '');
      }
    }, transitionDuration);

    // Return focus to trigger element
    hamburger.focus();
  }

  function _onEscapeKey(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      closeDrawer();
    }
  }

  if (hamburger)   hamburger.addEventListener('click', openDrawer);
  if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
  if (overlay)     overlay.addEventListener('click', closeDrawer);

  /* ── Drawer: sub-menu toggles ─────────────────────────────── */
  var drawerToggles = document.querySelectorAll(
    '.site-header__drawer-link--toggle'
  );

  drawerToggles.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var item    = btn.closest('.site-header__drawer-item');
      var subList = item ? item.querySelector('.site-header__drawer-sub') : null;
      if (!subList) return;

      var isOpen = item.classList.contains('is-open');

      // Close all other open sub-menus
      document.querySelectorAll(
        '.site-header__drawer-item.has-dropdown.is-open'
      ).forEach(function (openItem) {
        if (openItem !== item) {
          openItem.classList.remove('is-open');
          openItem.querySelector('.site-header__drawer-link--toggle')
            .setAttribute('aria-expanded', 'false');
          var sub = openItem.querySelector('.site-header__drawer-sub');
          if (sub) sub.setAttribute('hidden', '');
        }
      });

      if (isOpen) {
        item.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        subList.setAttribute('hidden', '');
      } else {
        item.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
        subList.removeAttribute('hidden');
      }
    });
  });

  /* ════════════════════════════════════════════════════════════
     DESKTOP DROPDOWN — keyboard accessibility
     CSS :hover already handles mouse; here we add keyboard support.
  ════════════════════════════════════════════════════════════ */

  var navItems = document.querySelectorAll(
    '.site-header__nav-item.has-dropdown'
  );

  navItems.forEach(function (item) {
    var trigger  = item.querySelector('.site-header__nav-link');
    var dropdown = item.querySelector('.site-header__dropdown');

    if (!trigger || !dropdown) return;

    // Open on Enter/Space when focused
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        var isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!isExpanded));
        if (!isExpanded) {
          // Focus first dropdown link
          var firstLink = dropdown.querySelector('.site-header__dropdown-link');
          if (firstLink) firstLink.focus();
        }
      }
    });

    // Close dropdown on Escape while focused inside
    item.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      }
    });

    // Sync aria-expanded with CSS :hover state
    item.addEventListener('mouseenter', function () {
      trigger.setAttribute('aria-expanded', 'true');
    });
    item.addEventListener('mouseleave', function () {
      trigger.setAttribute('aria-expanded', 'false');
    });

    // Close if focus leaves the item entirely
    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget)) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  });

  /* ════════════════════════════════════════════════════════════
     STICKY HEADER
  ════════════════════════════════════════════════════════════ */

  if (header) {
    var _scrolled = false;

    window.addEventListener('scroll', function () {
      var shouldScroll = window.scrollY > 10;
      if (shouldScroll !== _scrolled) {
        _scrolled = shouldScroll;
        header.classList.toggle('site-header--scrolled', _scrolled);
      }
    }, { passive: true });
  }

  /* ════════════════════════════════════════════════════════════
     CART BADGE — live update
     Listens for common Shopify AJAX cart events.
  ════════════════════════════════════════════════════════════ */

  function _updateCartBadge(count) {
    var badge = document.querySelector('[data-cart-count]');
    if (!badge) return;
    badge.textContent = count;
    // Also update aria-label on the parent cart link
    var cartLink = badge.closest('a');
    if (cartLink) {
      cartLink.setAttribute('aria-label', 'Cart, ' + count + ' items');
    }
    if (count === 0) {
      badge.classList.add('is-hidden');
    } else {
      badge.classList.remove('is-hidden');
    }
  }

  // Shopify Ajax Cart events (various theme/app implementations)
  document.addEventListener('cart:updated', function (e) {
    var count = e.detail && (e.detail.itemCount || e.detail.item_count || 0);
    _updateCartBadge(count);
  });

  document.addEventListener('cart:refresh', function (e) {
    var count = e.detail && (e.detail.itemCount || e.detail.item_count || 0);
    if (typeof count === 'number') _updateCartBadge(count);
  });

  // Shopify native fetch-based cart update
  document.addEventListener('shopify:section:load', function () {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (data) { _updateCartBadge(data.item_count || 0); })
      .catch(function () {});
  });

  /* ════════════════════════════════════════════════════════════
     FOCUS TRAP (accessibility — drawer)
  ════════════════════════════════════════════════════════════ */

  function _installFocusTrap(container) {
    var focusable = _getFocusable(container);
    if (!focusable.length) return;

    var first = focusable[0];
    var last  = focusable[focusable.length - 1];

    // Focus first element
    requestAnimationFrame(function () { first.focus(); });

    _trapHandler = function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener('keydown', _trapHandler);
  }

  function _removeFocusTrap(container) {
    if (_trapHandler) {
      container.removeEventListener('keydown', _trapHandler);
      _trapHandler = null;
    }
  }

  function _getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), ' +
        'select:not([disabled]), textarea:not([disabled]), ' +
        '[tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) {
      return !el.closest('[hidden]') && el.offsetParent !== null;
    });
  }

})();
